// ===== ロット フォーム (新規 / 編集) =====

import { useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, MenuItem, Paper, Stack, TextField, Typography } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { PageContext } from "../PageContext";
import Time from "../../../util/Time";
import { ContentLoading } from "../../../components/Loading";
import { MaterialMasterBase } from "../../../schema/generated/MaterialMaster";
import { ConstituentsById, LotRow, WorkMaterialFilterData, allowedMaterialIdsForWork } from "./helpers";
import { required } from "../../../util/typed";

export function LotFormPage({ pageContext, materials, constituentsById, workFilterData, mode }: { pageContext: PageContext; materials: MaterialMasterBase[]; constituentsById: ConstituentsById; workFilterData: WorkMaterialFilterData; mode: "new" | "edit" }) {
  const api = pageContext.api;
  const navigate = useNavigate();
  const { id: carryOutId, lotId } = useParams<{ id: string; lotId: string }>();
  const [filterWorkId, setFilterWorkId] = useState<number | "">("");
  const [materialId, setMaterialId] = useState<number | "">("");
  const [lotNo, setLotNo] = useState("");
  const [manufacturedAt, setManufacturedAt] = useState("");
  const [plannedCount, setPlannedCount] = useState<number>(0);
  const [remarks, setRemarks] = useState("");
  const [origLot, setOrigLot] = useState<LotRow | null>(null);
  const [loaded, setLoaded] = useState(mode === "new");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (mode === "new" || !carryOutId || !lotId) {
      return;
    }
    let active = true;
    api.carry_out.getFromId({ id: carryOutId }).then((co) => {
      if (!active) {
        return;
      }
      const lot = co.Lots.find((l) => l.id === lotId);
      if (lot) {
        setOrigLot(lot);
        setMaterialId(lot.material_id ?? "");
        setLotNo(lot.lot_no ?? "");
        setManufacturedAt(lot.manufactured_at != null ? new Time(lot.manufactured_at).ToDateInput() : "");
        setPlannedCount(lot.material_count);
        setRemarks(lot.remarks ?? "");
      }
      setLoaded(true);
    }).catch(() => { if (active) { setLoaded(true); } });
    return () => { active = false; };
  }, [api, mode, carryOutId, lotId]);

  const allowedMaterialIds = useMemo(() => allowedMaterialIdsForWork(workFilterData, filterWorkId), [workFilterData, filterWorkId]);

  const selectable = useMemo(() => {
    return materials
      .filter((m) => m.qr_managed && m.deleted_at == null)
      .filter((m) => allowedMaterialIds == null || allowedMaterialIds.has(m.id))
      .sort((a, b) => a.display_order - b.display_order);
  }, [materials, allowedMaterialIds]);

  const onFilterWorkChange = (next: number | "") => {
    setFilterWorkId(next);
    const allowed = allowedMaterialIdsForWork(workFilterData, next);
    if (allowed != null && materialId !== "" && !allowed.has(materialId)) {
      setMaterialId("");
    }
  };

  const submit = async () => {
    if (materialId === "") {
      setError("材料を選択してください");
      return;
    }
    if (!plannedCount || plannedCount < 1) {
      setError("予定QR数は 1 以上を指定してください");
      return;
    }
    let manufacturedEpoch: number | null | undefined = undefined;
    if (manufacturedAt.trim() !== "") {
      manufacturedEpoch = Time.DateInputToEpoch(manufacturedAt.trim());
      if (manufacturedEpoch == null) {
        setError("製造日の形式が不正です (例: 2026-07-15)");
        return;
      }
    }
    setSubmitting(true);
    try {
      if (mode === "new") {
        const res = await api.lot.put({
          carry_out_id: required(carryOutId, "URLの出荷ID"),
          optional_1: "",
          optional_2: 0,
          optional_3: 0,
          optional_4: 0,
          material_count: plannedCount,
          material_id: materialId,
          lot_no: lotNo.trim() || undefined,
          manufactured_at: manufacturedEpoch ?? undefined,
          remarks: remarks.trim() || undefined,
        });
        navigate(`/console/carry-out/${carryOutId}/lot/${res.id}/`);
      } else {
        await api.lot.patch({
          id: required(lotId, "URLのLOT ID"),
          carry_out_id: origLot?.carry_out_id ?? required(carryOutId, "URLの出荷ID"),
          optional_1: origLot?.optional_1 ?? "",
          optional_2: origLot?.optional_2 ?? 0,
          optional_3: origLot?.optional_3 ?? 0,
          optional_4: origLot?.optional_4 ?? 0,
          planned_count: origLot?.planned_count ?? 0,
          material_count: plannedCount,
          material_id: materialId,
          lot_no: lotNo.trim() || null,
          manufactured_at: manufacturedEpoch ?? null,
          remarks: remarks.trim() || null,
        });
        navigate(`/console/carry-out/${carryOutId}/lot/${lotId}/`);
      }
    } catch {
      setError("保存に失敗しました。もう一度お試しください");
      setSubmitting(false);
    }
  };

  if (!loaded) {
    return <ContentLoading />;
  }

  const selectedConstituents = materialId !== "" ? constituentsById.get(materialId) : undefined;
  const materialCountHelperText = allowedMaterialIds != null
    ? `${selectable.length} 件 表示中（選択した工事の使用予定材料）`
    : `${selectable.length} 件 表示中（QR 管理対象の全材料）`;

  return (
    <Stack p={3} gap={2} maxWidth={900} mx="auto">
      <Typography variant="h4" fontWeight="bold">材料ロット情報{mode === "edit" ? "の編集" : ""}</Typography>
      <Alert severity="info" sx={{ py: 0.5 }}>
        ロットは <b>材料単位の出荷</b> を表します。QR は <b>1 セット (主剤+硬化剤を物理パック化) に 1 枚</b> 貼付する運用です。メーカー LotNo と製造日は材料単位で1つ。構成細目はマスタで確定済みのため入力不要です。
      </Alert>
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Stack gap={2} maxWidth={600} mx="auto">
          <TextField select label="工事" value={filterWorkId}
            helperText="工事のエリア配下で使用予定の材料だけが下のプルダウンに表示されます。「すべて」を選ぶと絞り込みを解除します。"
            onChange={(e) => onFilterWorkChange(e.target.value === "" ? "" : Number(e.target.value))} fullWidth>
            <MenuItem value="">すべて</MenuItem>
            {workFilterData.works.map((w) => <MenuItem key={w.id} value={w.id}>{w.short_name || w.official_name}</MenuItem>)}
          </TextField>
          <TextField select label="材料" value={materialId} helperText={materialCountHelperText}
            onChange={(e) => setMaterialId(e.target.value === "" ? "" : Number(e.target.value))} fullWidth>
            <MenuItem value="">選択してください</MenuItem>
            {selectable.map((m) => <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>)}
          </TextField>
          {selectedConstituents && (selectedConstituents.constituents !== "" || selectedConstituents.capacity !== "") && (
            <Box sx={{ pl: 1.5, py: 1, borderLeft: 3, borderColor: "info.main", bgcolor: "grey.50" }}>
              <Typography variant="caption" color="text.secondary" display="block">構成細目（材料マスタ・参考）</Typography>
              <Typography variant="body2">{selectedConstituents.constituents || "-"}</Typography>
              {selectedConstituents.capacity !== "" && <Typography variant="caption" color="text.secondary">1セット容量: {selectedConstituents.capacity}</Typography>}
            </Box>
          )}
          <TextField label="LotNo" value={lotNo} onChange={(e) => setLotNo(e.target.value)} fullWidth />
          <TextField type="date" label="製造日" value={manufacturedAt} onChange={(e) => setManufacturedAt(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
          <TextField type="number" label="予定QR数" inputProps={{ min: 1, step: 1 }} value={plannedCount || ""} onChange={(e) => setPlannedCount(Math.max(0, Math.floor(Number(e.target.value) || 0)))} fullWidth />
          <TextField label="備考" multiline minRows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} fullWidth />
          {error && <Alert severity="error">{error}</Alert>}
          <Stack alignItems="center">
            <Button variant="contained" size="large" disabled={submitting} onClick={submit} sx={{ py: 1.5, width: "60%" }}>{mode === "new" ? "追加する" : "更新"}</Button>
          </Stack>
        </Stack>
      </Paper>
    </Stack>
  );
}
